import { Encoding, MarkType } from '@mode-vegas/vegas';
import * as angular from 'angular';
import type * as Highcharts from 'highcharts';
import { assign } from 'lodash';
import { Subscription } from 'rxjs';

import { Names } from '@mode-switch/model';
import { ChartRenderOptions } from '@mode/charts/domain';
import { computePalette, EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { EmbedActions, EmbedActionType } from '@mode/reports/sandbox/contract';
import { ChartTypes, InterActions } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT, generateUUID } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsProps } from '@mode/shared/util-segment';
import { isValidChartFormat } from '../../lib/chart-helpers';
import { ChartMenu } from '../../lib/charts/chart-menu';
import { ChartRenderer } from '../../lib/charts/chart-renderer';
import { ReportApiService } from '../../services/report-api-service';

enum RENDER_TYPE {
  designer = 'designer',
  report = 'report',
}

export class ChartRenderer2Controller implements angular.IController {
  static $inject = ['$element', '$window', 'ReportApiService'];

  renderStart: () => void;
  renderEnd: (args?: { $err: Error }) => void;

  renderModel: RenderDataModel;
  encoding: Encoding;

  token: string;
  chartType: ChartTypes.ChartType;

  preventExpandingInteraction?: boolean;
  format: ChartTypes.ChartFormat;
  renderContext: RENDER_TYPE;
  drilldownEnabled: boolean;
  enableContextExploration: boolean;

  currentContextMenuX: number;
  currentContextMenuY: number;
  hasIncompleteEncoding: boolean;
  suspendRendering: boolean;
  isWhiteLabel: boolean;
  isEmbedded: boolean;
  isGuest: boolean;
  isViewPage = false;
  hideContextMenu: boolean;
  onEmbedAction: (args: { $action: EmbedActions }) => void;

  private subscription?: Subscription;

  constructor(
    private $element: angular.IAugmentedJQuery,
    protected $window: angular.IWindowService,
    private reportApiService: ReportApiService
  ) {}

  $onInit() {
    // default render context to report view
    this.renderContext = this.renderContext || RENDER_TYPE.report;
    this.hideContextMenu = this.isWhiteLabel && (this.chartType === 'donut' || this.chartType === 'pie');
    this.preventExpandingInteraction = this.preventExpandingInteraction || this.isWhiteLabel;
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (this.suspendRendering) {
      return;
    }
    const modelChanged = changes.renderModel != null;
    const encodingChanged =
      changes.encoding && !angular.equals(changes.encoding.currentValue, changes.encoding.previousValue);
    const formatChanged = changes.format && !angular.equals(changes.format.currentValue, changes.format.previousValue);
    const typeChanged = changes.chartType != null;

    if (this.renderModel && (modelChanged || encodingChanged || formatChanged || typeChanged)) {
      this.enableContextExploration =
        this.renderContext === RENDER_TYPE.report && !this.isWhiteLabel && !this.isEmbedded && !this.isGuest;
      this.render().catch((err) => {
        // Shouldn't ever throw here, but just in case
        this.renderEnd({ $err: err });
      });
    }
  }

  $onDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async render() {
    this.hasIncompleteEncoding = false;
    this.renderStart();

    this.$element.find('.chart-render').html('');

    if (this.encoding == null) {
      this.renderEnd();
      return;
    }

    if (!EncodingHelpers.isChartEncodingComplete(this.encoding, this.chartType)) {
      console.warn('Chart encoding is incomplete'); // TODO: Remove me and replace with placeholder?
      this.hasIncompleteEncoding = true;
      this.renderEnd();
      return;
    }

    if (!isValidChartFormat(this.format)) {
      return;
    }

    this.$element.find('svg > *').remove();

    let namesOnX = false;
    let fieldOnSize = false;
    let multiSeries = false;
    let markType: MarkType = MarkType.Line;

    if (this.encoding.marks.series.length > 0) {
      markType = this.encoding.marks.series[0].properties.type.value;
    } else {
      markType = this.encoding.marks.properties.type.value;
    }

    // Normalize encoding to vertical chart for now
    const encoding = EncodingHelpers.ensureVerticalEncoding(this.encoding);

    if (encoding.x.length > 0 && encoding.x[0].formula === Names.Key) {
      namesOnX = true;
    }

    if (encoding.marks.series.length && encoding.marks.series[0].properties.size.values.length) {
      fieldOnSize = true;
    }

    if (encoding.marks.series.length > 1) {
      multiSeries = true;
    }

    // Generate menuOptions object and check if there are any items in context menu,
    // if not, disable `drilldownEnabled` in render options
    let menuOptions = ChartMenu.options({
      enableExploration: this.enableContextExploration,
      preventExpandingInteraction: this.isEmbedded || this.isWhiteLabel || this.preventExpandingInteraction,
    });

    const doesHaveItems = ChartMenu.hasItems(this.renderModel, encoding, menuOptions);
    if (!doesHaveItems) {
      this.drilldownEnabled = false;
    }

    let chart: Highcharts.Chart = null;
    const target = this.$element.find('.chart-render')[0];
    const chartFormat = this.format || DEFAULT_CHART_FORMAT;

    const palette = computePalette(chartFormat);
    let options: ChartRenderOptions = {
      palette,
      reportLevelColorMap: {}, // TODO: Unused, but the boilerplate in the chart renderer functions may be useful shortly for the colors feature
      containerHeight: this.$element.height(),
      chartToken: this.token,
      handler: (action: InterActions.Union) => {
        this.reportApiService.dispatch('chart_' + this.token, action);
      },
      drilldownEnabled: this.drilldownEnabled,
      complete: () => this.renderEnd(),
      showOmittedNullMessaging: this.renderContext === RENDER_TYPE.designer,
    };

    if ((markType === MarkType.Pie || markType === MarkType.Donut) && this.renderContext === RENDER_TYPE.designer) {
      options = { ...options, size: 'medium' };
    }

    try {
      switch (markType) {
        case MarkType.Bar:
          const horizontal = this.chartType === 'hStackedBar' || this.chartType === 'hStackedBar100';
          const percentStacked = this.chartType === 'hStackedBar100' || this.chartType === 'stackedBar100';

          if (multiSeries) {
            chart = await ChartRenderer.renderComboChart(this.renderModel, encoding, chartFormat, target, options);
          } else if (namesOnX) {
            chart = await ChartRenderer.renderGroupedBarChart(
              this.renderModel,
              encoding,
              chartFormat,
              target,
              this.chartType === 'hBar',
              options
            );
          } else if (percentStacked) {
            chart = await ChartRenderer.renderStackedBar100Chart(
              this.renderModel,
              encoding,
              chartFormat,
              target,
              horizontal,
              options
            ); // @TODO: adjust `percentStacked`
          } else {
            chart = await ChartRenderer.renderStackedBarChart(
              this.renderModel,
              encoding,
              chartFormat,
              target,
              horizontal,
              options
            ); // @TODO: adjust `percentStacked`
          }
          break;
        case MarkType.Line:
          chart = await ChartRenderer.renderLineChart(this.renderModel, encoding, chartFormat, target, options);
          break;
        case MarkType.Area:
          chart = await ChartRenderer.renderAreaChart(this.renderModel, encoding, chartFormat, target, options);
          break;
        case MarkType.TotalArea:
          chart = await ChartRenderer.renderTotalAreaChart(this.renderModel, encoding, chartFormat, target, options);
          break;
        case MarkType.Circle:
          if (fieldOnSize) {
            chart = await ChartRenderer.renderBubbleChart(this.renderModel, encoding, chartFormat, target, options);
          } else {
            chart = await ChartRenderer.renderScatterChart(this.renderModel, encoding, chartFormat, target, options);
          }
          break;
        case MarkType.Pie:
          chart = await ChartRenderer.renderPieChart(this.renderModel, encoding, chartFormat, target, options);
          break;
        case MarkType.Donut:
          chart = await ChartRenderer.renderDonutChart(this.renderModel, encoding, chartFormat, target, options);
          break;
      }
    } catch (err) {
      this.renderEnd({ $err: err });
      return;
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.reportApiService.subscribe('chart_' + this.token, (action) => {
      switch (action.type) {
        case InterActions.Type.MarkClick:
          if (this.drilldownEnabled) {
            const { x, y } = action.event.point;
            // clear context menu if user clicks same mark again
            if (this.currentContextMenuX === x && this.currentContextMenuY === y) {
              ChartMenu.clear(chart);
              this.currentContextMenuX = null;
              this.currentContextMenuY = null;
              return;
            } else {
              const dispatch = (item: InterActions.ContextItem) => {
                this.reportApiService.dispatch('chart_' + this.token, {
                  item,
                  type: InterActions.Type.ContextClick,
                });
              };

              this.currentContextMenuX = x;
              this.currentContextMenuY = y;

              let analyticsEventOptions;

              if (this.renderContext === RENDER_TYPE.report) {
                const analyticsProps = this.getContextMenuAnalyticsOptions();

                this.onEmbedAction({
                  $action: {
                    type: EmbedActionType.ClickChartMark,
                    chartToken: this.token,
                    props: analyticsProps,
                  },
                });
                analyticsEventOptions = {
                  analyticsProps,
                  boundOnEmbedAction: this.onEmbedAction.bind(this),
                  generateUUID: generateUUID,
                  chartToken: this.token,
                  reportContext: true,
                };
                menuOptions = assign(menuOptions, analyticsEventOptions);
              }
              const displayChartMenu: boolean = !this.isEmbedded && !this.isWhiteLabel && !this.hideContextMenu;
              if (displayChartMenu) {
                ChartMenu.show(dispatch, action, chart, this.renderModel, encoding, menuOptions);
              }
            }
          }

          break;
        case InterActions.Type.ChartClick:
          this.currentContextMenuX = null;
          this.currentContextMenuY = null;
          ChartMenu.clear(chart);
          break;
        case InterActions.Type.ContextClick:
          switch (action.item.type) {
            case InterActions.ContextItemType.ExploreMark: {
              ChartMenu.clear(chart);

              this.onEmbedAction({
                $action: {
                  type: EmbedActionType.ExploreChartMark,
                  target: action.item.target,
                  filters: action.item.filters,
                  chartToken: this.token,
                },
              });
              break;
            }
            case InterActions.ContextItemType.Drilldown:
              ChartMenu.clear(chart);

              this.onEmbedAction({
                $action: {
                  type: EmbedActionType.DrillDown,
                  target: action.item.target,
                  chartToken: this.token,
                },
              });
              break;
            case InterActions.ContextItemType.Keep:
              ChartMenu.clear(chart);
              break;
            case InterActions.ContextItemType.Exclude:
              ChartMenu.clear(chart);
              break;
            case InterActions.ContextItemType.ViewData:
              ChartMenu.clear(chart);
              break;
          }
          break;
      }
    });

    return chart;
  }

  getContextMenuAnalyticsOptions(): AnalyticsProps {
    return {
      event_id: generateUUID(),
      product_context: AnalyticsEvent.Context.Report,
      sub_context: AnalyticsEvent.SubContext.Chart,
      chart_id: 'chart_' + this.token,
      chart_type: this.chartType,
    };
  }
}

export const ChartRenderer2Component: angular.IComponentOptions = {
  bindings: {
    token: '<',
    chartType: '<',
    renderContext: '@',
    drilldownEnabled: '<',
    renderModel: '<',
    format: '<',
    encoding: '<',
    isWhiteLabel: '<',
    isEmbedded: '<',
    isGuest: '<',
    suspendRendering: '<',
    isViewPage: '<?',
    onEmbedAction: '&',
    renderStart: '&',
    renderEnd: '&',
    preventExpandingInteraction: '<?',
  },
  controller: ChartRenderer2Controller,
  controllerAs: 'ctrl',
  template: `<div class="chart-render render-zone"></div>`,
};
